import React from 'react';
import {
  Box,
  VStack,
  Heading,
  Text,
  Button,
  Icon,
  Card,
  CardBody,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
} from '@chakra-ui/react';
import { AlertTriangle, RefreshCw } from 'lucide-react';
import { withTranslation } from 'react-i18next';

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    this.setState({
      error: error,
      errorInfo: errorInfo,
    });
    
    // Log error to console in development
    if (process.env.NODE_ENV === 'development') {
      console.error('Error Boundary caught an error:', error, errorInfo);
    }
  }

  handleReload = () => {
    window.location.reload();
  };

  render() {
    const { t } = this.props;
    if (this.state.hasError) {
      return (
        <Box minH="100vh" bg="gray.50" display="flex" alignItems="center" justifyContent="center" p={4}>
          <Card maxW="lg" bg="white">
            <CardBody>
              <VStack spacing={6} textAlign="center">
                <Icon as={AlertTriangle} boxSize={16} color="red.500" />
                
                <VStack spacing={2}>
                  <Heading size="lg" color="red.600">{t('ui:errorBoundary.title')}</Heading>
                  <Text color="gray.600">
                    {t('ui:errorBoundary.description')}
                  </Text>
                </VStack>

                {process.env.NODE_ENV === 'development' && this.state.error && (
                  <Alert status="error" borderRadius="md" textAlign="left">
                    <AlertIcon />
                    <Box>
                      <AlertTitle>{t('ui:errorBoundary.devDetailsTitle')}</AlertTitle>
                      <AlertDescription>
                        <Text fontSize="sm" fontFamily="mono" mt={2}>
                          {this.state.error.toString()}
                        </Text>
                        {this.state.errorInfo && (
                          <Text fontSize="xs" fontFamily="mono" mt={2} color="gray.600">
                            {this.state.errorInfo.componentStack}
                          </Text>
                        )}
                      </AlertDescription>
                    </Box>
                  </Alert>
                )}

                <VStack spacing={2}>
                  <Button
                    colorScheme="blue"
                    leftIcon={<RefreshCw />}
                    onClick={this.handleReload}
                    size="lg"
                  >
                    {t('ui:errorBoundary.refreshPage')}
                  </Button>
                  
                  <Text fontSize="sm" color="gray.500">
                    {t('ui:errorBoundary.contactSupport')}
                  </Text>
                </VStack>
              </VStack>
            </CardBody>
          </Card>
        </Box>
      );
    }

    return this.props.children;
  }
}

// withTranslation re-renders the fallback when the UI language changes.
export default withTranslation()(ErrorBoundary);